import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X, Trophy, Users, CheckCircle, AlertCircle, Calendar, User, Target, Upload } from 'lucide-react';
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persianFa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import gregorianEn from "react-date-object/locales/gregorian_en";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import Button from '../UI/Button';
import Input from '../UI/Input';
import matchService from '../../services/matchService';
import { getFullImageUrl } from '../../utils/imageUpload';
import toast from 'react-hot-toast';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
  max-height: 100vh;
  overflow-y: auto;
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 16px;
  max-width: 900px;
  width: 100%;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px;
  border-bottom: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
  position: sticky;
  top: 0;
  z-index: 10;
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #b8b8d1;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #ff6b6b;
  }
`;

const ModalBody = styled.div`
  padding: 25px;
`;

const DateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DateGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DateLabel = styled.label`
  color: #b8b8d1;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DatePickerContainer = styled.div`
  .rmdp-container {
    width: 100%;
  }
  
  .rmdp-input {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    border: 1px solid #2d2d44;
    border-radius: 8px;
    padding: 12px 15px;
    color: #ffffff;
    font-size: 16px;
    width: 100%;
    
    &:focus {
      outline: none;
      border-color: #00d4ff;
      box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.2);
    }
  }
`;

const SectionTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TeamsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TeamCard = styled.div`
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border: 2px solid ${props => props.borderColor || '#2d2d44'};
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
`;

const TeamHeader = styled.div`
  background: linear-gradient(90deg, #00d4ff 0%, #4ecdc4 100%);
  color: white;
  padding: 15px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
`;

const TeamBody = styled.div`
  padding: 20px;
`;

const RankRow = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  padding: 15px;
  background: linear-gradient(90deg, rgba(0, 212, 255, 0.1) 0%, transparent 100%);
  border-radius: 8px;
  border: 1px solid rgba(0, 212, 255, 0.2);
`;

const RankLabel = styled.label`
  color: #b8b8d1;
  font-weight: 600;
  min-width: 100px;
`;

const RankInput = styled.input`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 6px;
  padding: 8px 12px;
  color: #ffffff;
  font-size: 16px;
  width: 80px;
  text-align: center;
  
  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.2);
  }
`;

const PlayersSection = styled.div`
  margin-top: 15px;
`;

const PlayersTitle = styled.h6`
  color: #6c6c8a;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 15px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PlayersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
`;

const PlayerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 12px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 8px;
`;

const PlayerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
`;

const PlayerAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #2d2d44;
`;

const PlayerDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PlayerName = styled.div`
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
`;

const PlayerGameName = styled.div`
  color: #6c6c8a;
  font-size: 12px;
`;

const KillInput = styled.input`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 6px;
  padding: 8px 12px;
  color: #ffffff;
  font-size: 16px;
  width: 70px;
  text-align: center;
  
  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.2);
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  margin: 20px 0;
  padding: 15px;
  background: linear-gradient(90deg, rgba(255, 107, 107, 0.1) 0%, transparent 100%);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #b8b8d1;
  flex-direction: column;
  gap: 15px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #2d2d44;
`;

const MatchResultModal = ({ 
  show, 
  match, 
  onClose, 
  onSuccess 
}) => {
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [rowedTeams, setRowedTeams] = useState([]);
  const [playerKills, setPlayerKills] = useState([]);
  const [startedDate, setStartedDate] = useState('');
  const [finishedDate, setFinishedDate] = useState('');
  const [startedDateJalali, setStartedDateJalali] = useState(null);
  const [finishedDateJalali, setFinishedDateJalali] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const [autoLoading, setAutoLoading] = useState(false);

  const fetchMatchDetails = async () => {
    try {
      setLoading(true);
      const data = await matchService.getMatch(match?.id);
      if (data && data.playersTeamList) {
        setTeams(data.playersTeamList.map(team => ({ id: team.id, name: team.teamName })));
        setPlayers(data.playersTeamList.flatMap(team => team.teamPlayers.map(player => ({
          id: player.id,
          username: player.username,
          gameUsername: player.gameUsername,
          picture: player.picture,
          teamId: team.id,
          teamName: team.teamName
        }))));
        setRowedTeams(data.playersTeamList.map(team => ({ teamId: team.id, finalRow: '' })));
        setPlayerKills(data.playersTeamList.flatMap(team => team.teamPlayers.map(player => ({ playerId: player.id, killCount: '' }))));
        setStartedDate(data.startedDate || '');
        setFinishedDate(data.finishedDate || '');
      }
    } catch (err) {
      setError('خطا در بارگذاری جزئیات مسابقه');
      console.error('Error fetching match details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && match?.id) {
      fetchMatchDetails();
    }
  }, [show, match?.id]);

  useEffect(() => {
    if (startedDate) {
      try {
        setStartedDateJalali(
          new DateObject({
            date: startedDate,
            calendar: gregorian,
            locale: gregorianEn
          }).convert(persian, persianFa)
        );
      } catch (error) {
        console.error('Error converting started date:', error);
        setStartedDateJalali(null);
      }
    } else {
      setStartedDateJalali(null);
    }
    if (finishedDate) {
      try {
        setFinishedDateJalali(
          new DateObject({
            date: finishedDate,
            calendar: gregorian,
            locale: gregorianEn
          }).convert(persian, persianFa)
        );
      } catch (error) {
        console.error('Error converting finished date:', error);
        setFinishedDateJalali(null);
      }
    } else {
      setFinishedDateJalali(null);
    }
  }, [startedDate, finishedDate]);

  const handleRowChange = (teamId, value) => {
    setRowedTeams(prev => prev.map(rt => rt.teamId === teamId ? { ...rt, finalRow: value } : rt));
  };

  const handleKillChange = (playerId, value) => {
    setPlayerKills(prev => prev.map(pk => pk.playerId === playerId ? { ...pk, killCount: value } : pk));
  };

  const handleStartedDateChange = (date) => {
    setStartedDateJalali(date);
    if (date) {
      try {
        const gregorianDate = date.convert(gregorian, gregorianEn);
        setStartedDate(gregorianDate.format("YYYY-MM-DDTHH:mm:ss"));
      } catch (error) {
        console.error('Error converting started date:', error);
        setStartedDate('');
      }
    } else {
      setStartedDate('');
    }
  };

  const handleFinishedDateChange = (date) => {
    setFinishedDateJalali(date);
    if (date) {
      try {
        const gregorianDate = date.convert(gregorian, gregorianEn);
        setFinishedDate(gregorianDate.format("YYYY-MM-DDTHH:mm:ss"));
      } catch (error) {
        console.error('Error converting finished date:', error);
        setFinishedDate('');
      }
    } else {
      setFinishedDate('');
    }
  };

  const populateFromAutoResult = (autoResult) => {
    if (!autoResult) return;
    if (Array.isArray(autoResult.rowedTeams)) {
      setRowedTeams(autoResult.rowedTeams.map(rt => ({
        teamId: rt.teamId,
        finalRow: String(rt.finalRow ?? '')
      })));
    }
    if (Array.isArray(autoResult.playerKills)) {
      setPlayerKills(autoResult.playerKills.map(pk => ({
        playerId: pk.playerId,
        killCount: String(pk.killCount ?? '')
      })));
    }
    if (autoResult.startedDate) setStartedDate(autoResult.startedDate);
    if (autoResult.finishedDate) setFinishedDate(autoResult.finishedDate);
  };

  const handleUploadJsonClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !match?.id) return;
    setError('');
    setAutoLoading(true);
    try {
      const response = await matchService.uploadAutoResultJson(match.id, file);
      // response expected to be the auto result object
      populateFromAutoResult(response);
      toast.success('نتیجه پیشنهادی از فایل بارگذاری شد. لطفاً بررسی و تایید کنید');
    } catch (err) {
      setError(err.message || 'خطا در بارگذاری فایل نتیجه');
      console.error('Error uploading result JSON:', err);
    } finally {
      setAutoLoading(false);
      // reset input so selecting the same file again works
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const hasDuplicateRows = () => {
    const rows = rowedTeams.map(rt => rt.finalRow).filter(Boolean);
    return new Set(rows).size !== rows.length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (hasDuplicateRows()) {
      setError('هر تیم باید رتبه نهایی منحصر به فردی داشته باشد');
      return;
    }
    
    if (rowedTeams.some(rt => !rt.finalRow)) {
      setError('لطفاً رتبه نهایی هر تیم را وارد کنید');
      return;
    }
    
    if (playerKills.some(pk => pk.killCount === '')) {
      setError('لطفاً تعداد کشتار هر بازیکن را وارد کنید');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        matchId: match.id,
        rowedTeams: rowedTeams.map(rt => ({ teamId: rt.teamId, finalRow: Number(rt.finalRow) })),
        playerKills: playerKills.map(pk => ({ playerId: pk.playerId, killCount: Number(pk.killCount) })),
        startedDate,
        finishedDate,
      };
      await matchService.addMatchResult(payload);
      toast.success('نتیجه مسابقه با موفقیت ثبت شد');
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (err) {
      setError(err.message || 'خطا در ثبت نتیجه مسابقه');
      console.error('Error saving match result:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group players by team
  const playersByTeam = teams.map(team => ({
    ...team,
    players: players.filter(player => player.teamId === team.id)
  }));

  // Helper function to get border color based on team index
  const getBorderColor = (index) => {
    const colors = ['#00d4ff', '#4ecdc4', '#ffd93d', '#ff6b6b'];
    return colors[index % colors.length];
  };

  if (!show || !match) return null;

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>
            <Trophy size={24} />
            ثبت نتیجه مسابقه
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {error && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {error}
            </ErrorMessage>
          )}

          {loading ? (
            <LoadingSpinner>
              <div>در حال بارگذاری جزئیات مسابقه...</div>
            </LoadingSpinner>
          ) : (
            <form onSubmit={handleSubmit}>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json"
                style={{ display: 'none' }}
                onChange={handleFileSelected}
              />

              <ActionButtons style={{ justifyContent: 'flex-start', marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleUploadJsonClick}
                  disabled={autoLoading || loading}
                  icon={<Upload size={16} />}
                >
                  {autoLoading ? 'در حال خواندن فایل...' : 'بارگذاری JSON نتیجه'}
                </Button>
              </ActionButtons>
              <DateRow>
                <DateGroup>
                  <DateLabel>
                    <Calendar size={16} />
                    تاریخ شروع
                  </DateLabel>
                  <DatePickerContainer>
                    <DatePicker
                      value={startedDateJalali}
                      onChange={handleStartedDateChange}
                      calendar={persian}
                      locale={persianFa}
                      format="YYYY/MM/DD HH:mm:ss"
                      plugins={[<TimePicker key="time_picker_started" position="bottom" />]}
                    />
                  </DatePickerContainer>
                </DateGroup>
                
                <DateGroup>
                  <DateLabel>
                    <Calendar size={16} />
                    تاریخ پایان
                  </DateLabel>
                  <DatePickerContainer>
                    <DatePicker
                      value={finishedDateJalali}
                      onChange={handleFinishedDateChange}
                      calendar={persian}
                      locale={persianFa}
                      format="YYYY/MM/DD HH:mm:ss"
                      plugins={[<TimePicker key="time_picker_finished" position="bottom" />]}
                    />
                  </DatePickerContainer>
                </DateGroup>
              </DateRow>

              <SectionTitle>
                <Trophy size={20} />
                رتبه‌بندی تیم‌ها و کشتار بازیکنان
              </SectionTitle>

              <TeamsContainer>
                {playersByTeam.map((team, index) => (
                  <TeamCard key={team.id} borderColor={getBorderColor(index)}>
                    <TeamHeader>
                      <Users size={16} />
                      {team.name}
                    </TeamHeader>
                    <TeamBody>
                      <RankRow>
                        <RankLabel>
                          <Trophy size={16} />
                          رتبه نهایی:
                        </RankLabel>
                        <RankInput
                          type="number"
                          min={1}
                          value={rowedTeams.find(rt => rt.teamId === team.id)?.finalRow || ''}
                          onChange={e => handleRowChange(team.id, e.target.value)}
                          placeholder="رتبه"
                          required
                        />
                      </RankRow>

                      <PlayersSection>
                        <PlayersTitle>
                          <User size={14} />
                          کشتار بازیکنان:
                        </PlayersTitle>
                        <PlayersGrid>
                          {team.players.map(player => (
                            <PlayerRow key={player.id}>
                              <PlayerInfo>
                                {player.picture && (
                                  <PlayerAvatar
                                    src={getFullImageUrl(player.picture)}
                                    alt={player.username}
                                  />
                                )}
                                <PlayerDetails>
                                  <PlayerName>{player.username}</PlayerName>
                                  {player.gameUsername && (
                                    <PlayerGameName>@{player.gameUsername}</PlayerGameName>
                                  )}
                                </PlayerDetails>
                              </PlayerInfo>
                              <KillInput
                                type="number"
                                min={0}
                                value={playerKills.find(pk => pk.playerId === player.id)?.killCount || ''}
                                onChange={e => handleKillChange(player.id, e.target.value)}
                                placeholder="کشتار"
                                required
                              />
                            </PlayerRow>
                          ))}
                        </PlayersGrid>
                      </PlayersSection>
                    </TeamBody>
                  </TeamCard>
                ))}
              </TeamsContainer>

              <ActionButtons>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={onClose}
                  disabled={loading}
                  icon={<X size={16} />}
                >
                  انصراف
                </Button>
                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={loading || autoLoading}
                  icon={<CheckCircle size={16} />}
                >
                  {loading ? 'در حال ثبت...' : 'ثبت نتیجه'}
                </Button>
              </ActionButtons>
            </form>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default MatchResultModal;